/*
 * SimplyInked drawer upsells (HS Slide Cart).
 * Adds, inside the app-rendered drawer:
 *   1. a "switch to N and ship free" button under the free-shipping bar,
 *   2. a "Switch to N for +$X" button on each custom-tattoo line below the 10-pack,
 *   3. a rush-production toggle when a custom tattoo is in the cart,
 *   4. a seasonal cross-sell (Halloween) until the configured end date.
 * Every swap adds the new line before removing the old one, and copies the
 * line's properties (artwork upload, notes, use case) to the new line.
 * Turning rush off for everyone: set the "Rush Production (24 Hours)" product to Draft.
 */
(() => {
  if (window.SIDrawerUpsells) return;
  window.SIDrawerUpsells = true;

  const configElement = document.getElementById('si-drawer-upsells-config');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch (_) { return; }

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const NEXT_TIER = { 3: 5, 5: 10 };
  const PREFIX = 'si-du';

  const money = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const whole = cents % 100 === 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency,
      minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2
    }).format(cents / 100);
  };

  const countOf = (option) => {
    const match = /(\d+)\s*Tattoos/i.exec(option || '');
    return match ? Number(match[1]) : 0;
  };

  const productCache = {};
  function loadProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productCache[handle]) {
      productCache[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
    }
    return productCache[handle];
  }

  async function cartRequest(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
    if (!response.ok) throw new Error(path + ' ' + response.status);
    return response.json();
  }

  function refreshDrawer() {
    try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: {} })); } catch (_) {}
    if (typeof window.HS_SLIDE_CART_UPDATE === 'function') {
      window.HS_SLIDE_CART_UPDATE();
    } else {
      window.location.reload();
    }
  }

  function setBusy(button, text) {
    button.disabled = true;
    button.dataset.label = button.textContent;
    button.textContent = text;
  }

  function resetBusy(button, text) {
    button.disabled = false;
    button.textContent = text || button.dataset.label || button.textContent;
  }

  // Swap a custom-tattoo line to another pack of the same size, keeping its artwork and notes.
  async function swapLine(line, target, button) {
    setBusy(button, 'Updating…');
    try {
      const properties = Object.assign({}, line.properties || {});
      if (config.packLabel) properties[config.packLabel] = target.count + ' tattoos · ' + target.variant.option1;
      await cartRequest('cart/add.js', { items: [{ id: target.variant.id, quantity: line.quantity, properties }] });
      await cartRequest('cart/change.js', { id: line.key, quantity: 0 });
      refreshDrawer();
    } catch (_) {
      resetBusy(button, 'Try again');
    }
  }

  async function setRush(rushVariantId, on, input) {
    input.disabled = true;
    try {
      if (on) {
        await cartRequest('cart/add.js', { items: [{ id: rushVariantId, quantity: 1 }] });
      } else {
        const cart = await cartRequest('cart.js');
        const lines = cart.items.filter((item) => item.variant_id === rushVariantId);
        for (const item of lines) await cartRequest('cart/change.js', { id: item.key, quantity: 0 });
      }
      refreshDrawer();
    } catch (_) {
      input.checked = !on;
      input.disabled = false;
    }
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function upgradeFor(line, customProduct) {
    const variant = customProduct.variants.find((item) => item.id === line.variant_id);
    if (!variant) return null;
    const count = countOf(variant.option2);
    const nextCount = NEXT_TIER[count];
    if (!nextCount) return null;
    const next = customProduct.variants.find((item) =>
      item.option1 === variant.option1 && countOf(item.option2) === nextCount && item.available);
    if (!next) return null;
    return { variant: next, count: nextCount, delta: (next.price - variant.price) * line.quantity };
  }

  function halloweenHandles() {
    const data = document.getElementById('si-halloween-cart-data');
    if (!data) return new Set();
    try {
      return new Set((JSON.parse(data.textContent).products || []).map((product) => product.handle));
    } catch (_) {
      return new Set();
    }
  }

  let lastDomSignature = '';
  let rendering = false;
  let again = false;

  // Cheap check from the drawer's own markup: which lines it shows, and whether our pieces are still there.
  function domSignature(layout) {
    const rows = [...layout.querySelectorAll('.hs-sticky-cart .hs-item-cart-content[data-item-key]')]
      .map((row) => row.getAttribute('data-item-key') + ':' + row.getAttribute('data-item-qty')).join('|');
    return rows + '#' + layout.querySelectorAll('.' + PREFIX).length;
  }

  async function render() {
    const layout = document.querySelector('.hs-popup-cart-sp-load .hs-layout-content');
    if (!layout) return;
    if (rendering) { again = true; return; }
    if (lastDomSignature && domSignature(layout) === lastDomSignature) return;
    rendering = true;
    try {
      const [cart, customProduct, rushProduct, seasonalProduct] = await Promise.all([
        cartRequest('cart.js'),
        loadProduct(config.customHandle),
        loadProduct(config.rushHandle),
        Date.now() < Date.parse(config.seasonalEnd || 0) ? loadProduct(config.seasonalHandle) : Promise.resolve(null)
      ]);

      // Rebuild every time the drawer's markup changed, so pieces the app redraws away come back.
      layout.querySelectorAll('.' + PREFIX).forEach((node) => node.remove());
      document.body.classList.remove(PREFIX + '-seasonal-on');

      const customLines = customProduct ? cart.items.filter((item) => item.product_id === customProduct.id) : [];
      const upgrades = new Map();
      customLines.forEach((line) => {
        const upgrade = upgradeFor(line, customProduct);
        if (upgrade) upgrades.set(line.key, { line, upgrade });
      });

      // 1. Free-shipping tier swap, under the app's free-shipping bar.
      let barKey = null;
      const gap = (config.freeShipThreshold || 0) - cart.items_subtotal_price;
      const rewards = layout.querySelector('[data-key="hs-rewards"]');
      if (gap > 0 && rewards && upgrades.size) {
        const closing = [...upgrades.values()]
          .filter((entry) => entry.upgrade.delta >= gap)
          .sort((a, b) => a.upgrade.delta - b.upgrade.delta)[0];
        if (closing) {
          barKey = closing.line.key;
          const box = element('div', PREFIX + ' ' + PREFIX + '-ship');
          box.appendChild(element('p', PREFIX + '-ship-text',
            'Switch to ' + closing.upgrade.count + ' tattoos (+' + money(closing.upgrade.delta) + ') and it ships free.'));
          const button = element('button', PREFIX + '-btn', 'Switch to ' + closing.upgrade.count + ' tattoos');
          button.type = 'button';
          button.addEventListener('click', () => swapLine(closing.line, closing.upgrade, button));
          box.appendChild(button);
          rewards.insertAdjacentElement('afterend', box);
        }
      }

      // 2. Inline upgrade on each custom line below the 10-pack.
      upgrades.forEach((entry, key) => {
        if (key === barKey) return;
        const row = layout.querySelector('.hs-item-cart-content[data-item-key="' + key + '"]');
        const holder = row && (row.querySelector('.hs-item-cart-description') || row);
        if (!holder) return;
        const button = element('button', PREFIX + ' ' + PREFIX + '-line-btn',
          'Switch to ' + entry.upgrade.count + ' for +' + money(entry.upgrade.delta));
        button.type = 'button';
        button.addEventListener('click', () => swapLine(entry.line, entry.upgrade, button));
        holder.appendChild(button);
      });

      // 3. Rush production toggle (only while the rush product is available).
      const products = layout.querySelector('.hs-sticky-cart[data-key="hs-products"]');
      if (rushProduct && rushProduct.available && customLines.length && products) {
        const rushVariant = rushProduct.variants[0];
        const inCart = cart.items.some((item) => item.variant_id === rushVariant.id);
        const label = element('label', PREFIX + ' ' + PREFIX + '-rush');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = inCart;
        input.addEventListener('change', () => setRush(rushVariant.id, input.checked, input));
        const text = element('span', '');
        text.appendChild(element('strong', '', 'Rush production'));
        text.appendChild(document.createTextNode(' Made within 24 hours (+' + money(rushVariant.price) + ')'));
        label.appendChild(input);
        label.appendChild(text);
        products.insertAdjacentElement('afterend', label);
      }

      // 4. Seasonal cross-sell, replacing the app's manual upsell while it runs.
      //    Placement (after the cart lines, full width) is set in si-drawer-upsells.css.
      const halloween = halloweenHandles();
      const cartHasSeasonal = cart.items.some((item) => halloween.has(item.handle) || (seasonalProduct && item.product_id === seasonalProduct.id));
      const appUpsell = layout.querySelector('[data-key="hs-upsellManually"]');
      // Oct 12 2026: no seasonal add-on next to Chapters or Chapters Club; the app's Chapter companion upsell shows instead.
      const cartHasChapters = cart.items.some((item) => /-symbol-collection$/.test(item.handle || '') ||
        item.handle === 'chapters-club-3-curated-chapters' ||
        (item.product_type || '').toLowerCase() === 'chapters');
      if (seasonalProduct && seasonalProduct.available && !cartHasSeasonal && !cartHasChapters && cart.item_count > 0) {
        const variant = seasonalProduct.variants.find((item) => item.available);
        if (variant) {
          const card = element('div', PREFIX + ' ' + PREFIX + '-seasonal');
          card.appendChild(element('p', PREFIX + '-eyebrow', config.seasonalEyebrow || 'Halloween'));
          const rowEl = element('div', PREFIX + '-seasonal-row');
          if (seasonalProduct.featured_image) {
            const image = document.createElement('img');
            image.src = seasonalProduct.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
            image.alt = '';
            image.loading = 'lazy';
            image.width = 56;
            image.height = 56;
            rowEl.appendChild(image);
          }
          const info = element('div', PREFIX + '-seasonal-info');
          info.appendChild(element('span', PREFIX + '-seasonal-title', seasonalProduct.title));
          info.appendChild(element('span', PREFIX + '-seasonal-price', money(variant.price)));
          rowEl.appendChild(info);
          const button = element('button', PREFIX + '-btn ' + PREFIX + '-btn--small', 'Add');
          button.type = 'button';
          button.setAttribute('aria-label', 'Add ' + seasonalProduct.title + ' to cart');
          button.addEventListener('click', async () => {
            setBusy(button, 'Adding…');
            try {
              await cartRequest('cart/add.js', { items: [{ id: variant.id, quantity: 1 }] });
              refreshDrawer();
            } catch (_) {
              resetBusy(button, 'Try again');
            }
          });
          rowEl.appendChild(button);
          card.appendChild(rowEl);
          if (appUpsell) appUpsell.insertAdjacentElement('beforebegin', card);
          else layout.appendChild(card);
          document.body.classList.add(PREFIX + '-seasonal-on');
        }
      }
    } catch (_) {
      // Leave the app's drawer exactly as it is if anything here fails.
    } finally {
      lastDomSignature = domSignature(layout);
      rendering = false;
      if (again) { again = false; schedule(); }
    }
  }

  let timer = null;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(render, 150);
  }

  new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) => {
      const target = mutation.target;
      if (target.nodeType === 1 && target.closest && target.closest('.' + PREFIX + ', .swiper, .hs-cross-sell, [data-key="hs-announcement"]')) return false;
      const onlyOurs = mutation.addedNodes.length > 0 &&
        [...mutation.addedNodes].every((node) => node.nodeType === 1 && node.classList && node.classList.contains(PREFIX));
      const removedOurs = mutation.removedNodes.length > 0 &&
        [...mutation.removedNodes].every((node) => node.nodeType === 1 && node.classList && node.classList.contains(PREFIX));
      if (onlyOurs || removedOurs) return false;
      if (target.closest && target.closest('.hs-popup-cart-sp-load')) return true;
      return [...mutation.addedNodes].some((node) => node.nodeType === 1 &&
        (node.matches('.hs-popup-cart-sp-load') || (node.querySelector && node.querySelector('.hs-popup-cart-sp-load'))));
    });
    if (relevant) schedule();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-item-qty'] });

  schedule();
})();